/**
 * 福井県の背景ツアー「福井めぐり」コンテンツ。
 *
 * 軸は季節。5月の一乗谷から始めて、1年かけて翌年4月の丸岡城へ戻る。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 東尋坊の「世界三大絶勝」、氣比神宮の「日本三大鳥居」、越前水仙の「日本三大群生地」は
 *    いずれも根拠となる公的指定が確認できない俗称。書かない。
 *  - 氣比神宮の大鳥居は正保2年（1645）建立。702年は神宮側の伝承で、現存鳥居の年ではない。
 *  - 丸岡城は天守改修工事中で入城できない。「登れる」と書かない。
 *  - 一乗谷の「日本のポンペイ」は比喩。使わない。
 *  - 越前水仙の畑は私有地。立ち入り・摘み取りを促さない。
 *  - 蘇洞門は遊覧船でしか見られず、冬季は欠航が普通。
 */

import type { StageTour } from '../types';

export const fukuiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'ichijodani',
      name: '一乗谷朝倉氏遺跡',
      area: '福井市・5月の朝',
      image: '/assets/scenery/fukui/scenes/01_ichijodani.webp',
      hotspots: [
        {
          id: 'ichijodani-machinami',
          x: 0.48,
          y: 0.42,
          title: '道まで掘り出された町',
          body: '武家屋敷・寺院・町屋・職人屋敷や道路に至るまで、戦国時代の町並がほぼ完全な姿で発掘されました。',
          source: 'https://fuku-iro.jp/spot/detail_10002.html',
          mapQuery: '一乗谷朝倉氏遺跡',
        },
        {
          id: 'ichijodani-shitei',
          x: 0.74,
          y: 0.28,
          title: '三重の指定',
          body: '遺跡は国の特別史跡・特別名勝に指定され、出土品は重要文化財に指定されています。復原町並は9時から17時（入館は16時30分まで）です。',
          source: 'https://fuku-iro.jp/spot/detail_10002.html',
          mapQuery: '一乗谷 復原町並',
        },
      ],
    },
    {
      id: 'eiheiji',
      name: '永平寺',
      area: '永平寺町・6月の午前',
      image: '/assets/scenery/fukui/scenes/02_eiheiji.webp',
      hotspots: [
        {
          id: 'eiheiji-dogen',
          x: 0.5,
          y: 0.34,
          title: '寛元2年、道元が開いた',
          body: '寛元2年（1244年）に道元禅師が開いた禅の修行道場です。深山幽谷の境内に、70余りもの殿堂楼閣が建ち並びます。',
          source: 'https://fuku-iro.jp/spot/detail_10026.html',
          mapQuery: '大本山永平寺',
        },
        {
          id: 'eiheiji-sanpai',
          x: 0.24,
          y: 0.5,
          title: 'ここは観光施設ではなく修行道場',
          body: '開門8時30分、閉門16時30分。ベビーカーでの参拝は安全のため控えるよう求められ、ペットは介助犬以外ケージに入れたままで、写真のために外へ出すこともできません。',
          source: 'https://daihonzan-eiheiji.com/sanpai.html',
          mapQuery: '永平寺 参拝',
        },
      ],
    },
    {
      id: 'mikatagoko',
      name: '三方五湖',
      area: '美浜町・7月の昼',
      image: '/assets/scenery/fukui/scenes/03_mikatagoko.webp',
      hotspots: [
        {
          id: 'mikata-goko',
          x: 0.5,
          y: 0.44,
          title: '五色の湖',
          body: '三方湖は五湖でただ一つの完全淡水湖、水月湖と菅湖は汽水湖、久々子湖は汽水湖ですが満潮時には日本海から海水が逆流して塩水にもなり、日向湖は完全な塩水湖です。水質や水深が違い、すべて濃さの違う青色に見えることから「五色の湖」と呼ばれています。',
          source:
            'https://www.town.fukui-wakasa.lg.jp/soshiki/kankoumatizukurika/gyomuannai/1/4/3229.html',
          mapQuery: '三方五湖',
        },
        {
          id: 'mikata-rainbow',
          x: 0.76,
          y: 0.28,
          title: '山頂公園は有料',
          body: 'レインボーラインは令和4年（2022年）10月1日から無料開放されましたが、山頂公園へはリフトかケーブルカーで上がり、入園料はリフト・ケーブルの往復乗車料込みで大人（中学生以上）900円、小人（小学生）500円です。テラスは5か所あります。',
          source:
            'https://www.town.fukui-wakasa.lg.jp/soshiki/kankoumatizukurika/gyomuannai/1/5/3232.html',
          mapQuery: 'レインボーライン山頂公園',
        },
      ],
    },
    {
      id: 'sotomo',
      name: '蘇洞門',
      area: '小浜市・8月の昼',
      image: '/assets/scenery/fukui/scenes/04_sotomo.webp',
      hotspots: [
        {
          id: 'sotomo-daimon',
          x: 0.46,
          y: 0.44,
          title: '大門・小門',
          body: 'およそ6キロにわたる断崖に、大門・小門と名づけられた奇岩や洞門、洞窟が連なります。波が岩に穴を開けたものです。',
          source: 'https://www.fuku-e.com/spot/detail_1474.html',
          mapQuery: '蘇洞門',
        },
        {
          id: 'sotomo-fune',
          x: 0.74,
          y: 0.6,
          title: '遊覧船でめぐる',
          body: '蘇洞門めぐり遊覧船は、日本海の荒波がつくりあげたダイナミックな景観を見る約60分のクルージングです。波や風の条件が良い時は上陸もできます。定休日は12月から2月です。',
          source: 'https://wakasa-obama.jp/spot/sotomo-pleasure-boat/',
          mapQuery: '蘇洞門めぐり遊覧船',
        },
      ],
    },
    {
      id: 'kyoryu-hakubutsukan',
      name: '福井県立恐竜博物館',
      area: '勝山市・9月の午後',
      image: '/assets/scenery/fukui/scenes/05_kyoryu_hakubutsukan.webp',
      hotspots: [
        {
          id: 'kyoryu-zenshin',
          x: 0.5,
          y: 0.4,
          title: '全身骨格が51体',
          body: '館内には51体もの恐竜全身骨格をはじめ、化石やジオラマ、大迫力の復元模型などが数多く展示されています。',
          source: 'https://www.dinosaur.pref.fukui.jp/',
          mapQuery: '福井県立恐竜博物館',
        },
        {
          id: 'kyoryu-yoyaku',
          x: 0.24,
          y: 0.58,
          title: 'ふらっとは入れない',
          body: '入館には日時指定の観覧券が必要で、博物館のホームページから事前に購入します。当日分の観覧券は、事前の販売数に余裕がある場合だけ博物館の券売機でも販売されます。',
          source: 'https://www.dinosaur.pref.fukui.jp/',
          mapQuery: 'かつやま恐竜の森',
        },
      ],
    },
    {
      id: 'yume-no-kakehashi',
      name: '夢のかけはし',
      area: '大野市・10月の夕',
      image: '/assets/scenery/fukui/scenes/06_yume_no_kakehashi.webp',
      hotspots: [
        {
          id: 'kakehashi-nagasa',
          x: 0.5,
          y: 0.42,
          title: '九頭竜湖に架かる266メートル',
          body: '正式な名前は箱ヶ瀬橋。九頭竜湖にかかる全長266メートルの橋です。',
          source: 'https://www.city.ono.fukui.jp/kanko/kanko-joho/guide/yumenokakehashi.html',
          mapQuery: '夢のかけはし 九頭竜湖',
        },
        {
          id: 'kakehashi-setoohashi',
          x: 0.76,
          y: 0.28,
          title: '瀬戸大橋の試作品',
          body: '瀬戸大橋の試作品として建設されたことで知られます。橋と湖、季節によって色を変える周りの自然を眺められるドライブスポットです。',
          source: 'https://www.fuku-e.com/spot/detail_6039.html',
          mapQuery: '九頭竜湖',
        },
      ],
    },
    {
      id: 'tojinbo',
      name: '東尋坊',
      area: '坂井市・11月の夕',
      image: '/assets/scenery/fukui/scenes/07_tojinbo.webp',
      hotspots: [
        {
          id: 'tojinbo-chujo',
          x: 0.48,
          y: 0.46,
          title: '約1キロの柱状節理',
          body: '輝石安山岩の柱状節理が、海沿いにおよそ1キロ連なります。これほど広い範囲にあるのは世界に3か所ともいわれます。',
          source: 'https://www.city.fukui-sakai.lg.jp/kankou/kanko-bunka/kanko/shizen/tojinbo.html',
          mapQuery: '東尋坊',
        },
        {
          id: 'tojinbo-anzen',
          x: 0.24,
          y: 0.64,
          title: '崖っぷちに立つと',
          body: '東尋坊は約1キロにおよぶ大規模な柱状節理で、崖っぷちに立つと思わず足がすくんでしまいます。国の天然記念物に指定され、日本の地質百選にも選ばれています。',
          source: 'https://www.fuku-e.com/spot/detail_1476.html',
          mapQuery: '東尋坊タワー',
        },
      ],
    },
    {
      id: 'kehijingu',
      name: '氣比神宮',
      area: '敦賀市・12月の夜',
      image: '/assets/scenery/fukui/scenes/08_kehijingu.webp',
      hotspots: [
        {
          id: 'kehi-torii',
          x: 0.5,
          y: 0.4,
          title: '朱塗りの両部鳥居',
          body: '高さ36尺（10.9メートル）の木造両部鳥居で、朱漆で塗られています。正保2年（1645年）の建立で、明治34年に国宝に指定され、いまは国の重要文化財です。',
          source: 'https://kehijingu.jp/sp/about4.html',
          mapQuery: '氣比神宮',
        },
        {
          id: 'kehi-basho',
          x: 0.76,
          y: 0.56,
          title: '芭蕉も来た',
          body: '北陸道の総鎮守。元禄2年の旧暦8月に松尾芭蕉が訪れていて、境内には芭蕉の像と句碑があります。',
          source: 'https://tsuruga-kanko.jp/spot/shrines_temples/kehijingu-shrine/',
          mapQuery: '氣比神宮 芭蕉像',
        },
      ],
    },
    {
      id: 'echizen-suisen',
      name: '越前海岸の水仙畑',
      area: '越前町・1月の朝',
      image: '/assets/scenery/fukui/scenes/09_echizen_suisen.webp',
      hotspots: [
        {
          id: 'suisen-keikan',
          x: 0.48,
          y: 0.46,
          title: '海に迫る斜面の段々畑',
          body: 'かつて水田だった段々畑や急な斜面で、白い日本水仙が咲き誇ります。2021年3月、越前海岸の水仙畑の文化的景観が国の重要文化的景観に選定されました。',
          source: 'https://fuku-iro.jp/blog/detail_29.html',
          mapQuery: '越前海岸 水仙畑',
        },
        {
          id: 'suisen-kisetsu',
          x: 0.76,
          y: 0.3,
          title: '冬の海岸を白く彩る',
          body: '冬が訪れるころ、水仙たちは寒さから身を守るように寄り添いながら、海岸の風景を白くつつみ込みます。香りは甘く清楚で、ミカンの芳香にも似ています。',
          source: 'https://www.fuku-e.com/spot/detail_1518.html',
          mapQuery: '越前水仙',
        },
        {
          id: 'suisen-manner',
          x: 0.22,
          y: 0.62,
          title: '集落の人が暮らす場所',
          body: '水仙畑は重要文化財ではなく「重要文化的景観」。集落のみなさんが生活する場所なので、出会った人たちにはあいさつをしましょう。',
          source: 'https://fuku-iro.jp/blog/detail_29.html',
          mapQuery: '越前岬水仙ランド',
        },
      ],
    },
    {
      id: 'maruokajo',
      name: '丸岡城',
      area: '坂井市・4月の朝',
      image: '/assets/scenery/fukui/scenes/10_maruokajo.webp',
      hotspots: [
        {
          id: 'maruoka-ishigawara',
          x: 0.5,
          y: 0.32,
          title: '屋根が石でできている',
          body: '現存12天守のうち、屋根が石瓦で葺かれているのはここだけ。およそ6000枚のうち2割ほどが足羽山のふもとで採れる笏谷石です。国の重要文化財で、北陸で唯一の現存天守。',
          source: 'https://maruoka-castle.jp/highlights/',
          mapQuery: '丸岡城',
        },
        {
          id: 'maruoka-jishin',
          x: 0.26,
          y: 0.5,
          title: '地震で倒れ、元の姿に組み直した',
          body: '1948年の福井地震で石垣もろとも倒壊しましたが、昭和26年から30年の修理で震災前の姿に戻されました。築いたのは織田信長の家臣・柴田勝豊で、1576年とされます。',
          source: 'https://maruoka-castle.jp/history/',
          mapQuery: '丸岡城 一筆啓上',
        },
        {
          id: 'maruoka-kouji',
          x: 0.76,
          y: 0.56,
          title: 'いまは中に入れません',
          body: '天守が改修工事中のため、入城できません。外から眺めるかたちになります。訪ねる前に公式サイトで工事の状況を確かめてください。',
          source: 'https://maruoka-castle.jp/information/',
          mapQuery: '丸岡城 霞ヶ城公園',
        },
      ],
    },
  ],
};
